import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  Chip
} from '@mui/material';
// Firebase artık kullanılmıyor - SQL'e geçildi
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { mainApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';

interface QRMenuDomainModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
}

const QRMenuDomainModal: React.FC<QRMenuDomainModalProps> = ({
  open,
  onClose,
  companyId
}) => {
  const { t } = useTranslation('salesSystem');
  const [domain, setDomain] = useState('');
  const [currentDomain, setCurrentDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Mevcut domain'i yükle
  useEffect(() => {
    if (open && companyId) {
      loadCurrentDomain();
    }
  }, [open, companyId]);

  const loadCurrentDomain = async () => {
    if (!companyId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log(`🔄 SQL'den custom domain yükleniyor (Şirket: ${companyId})`);
      
      const response = await mainApi.get(`/api/QRMenu/custom-domain`);
      const result = response.data;
      console.log('🔍 [DEBUG] API Response:', JSON.stringify(result, null, 2));
      
      if (result.success && result.data) {
        let savedDomain = '';
        
        if (typeof result.data === 'string') {
          savedDomain = result.data;
        } else if (result.data.customDomain) {
          savedDomain = result.data.customDomain;
        } else if (result.data.domainName) {
          savedDomain = result.data.domainName;
        } else {
          console.warn('⚠️ Beklenmeyen data yapısı:', result.data);
          savedDomain = '';
        }
        
        console.log(`✅ Custom domain yüklendi: ${savedDomain}`);
        setCurrentDomain(savedDomain);
        setDomain(savedDomain);
      } else {
        console.warn('⚠️ API success:false veya data yok:', result);
        setCurrentDomain('');
        setDomain('');
      }
    } catch (err: any) {
      console.error('❌ Domain yükleme hatası:', err);
      if (err.response) {
        console.warn('❌ Custom domain yüklenemedi:', err.response.status);
        setCurrentDomain('');
        setDomain('');
      } else {
        setError(t('qrMenuDomainModal.errors.loadCurrentFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const validateDomain = (inputDomain: string): boolean => {
    if (!inputDomain.trim()) {
      setError(t('qrMenuDomainModal.errors.domainRequired'));
      return false;
    }

    // HTTP/HTTPS kontrolü - eğer varsa kaldır
    if (inputDomain.includes('http://') || inputDomain.includes('https://')) {
      setError(t('qrMenuDomainModal.errors.protocolNotAllowed'));
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!companyId) {
      setError(t('qrMenuDomainModal.errors.companyInfoMissing'));
      return;
    }

    const trimmedDomain = domain.trim().toLowerCase();
    
    if (!validateDomain(trimmedDomain)) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      console.log(`🔄 Custom domain SQL'e kaydediliyor: ${trimmedDomain} (Şirket: ${companyId})`);
      
      await mainApi.post(`/api/QRMenu/custom-domain`, {
        customDomain: trimmedDomain
      });
      
      console.log(`✅ Custom domain SQL'e kaydedildi: ${trimmedDomain}`);
      
      setCurrentDomain(trimmedDomain);
      setSuccess(true);
      
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('❌ Domain kaydetme API hatası:', err);
      setError(t('qrMenuDomainModal.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setDomain(currentDomain);
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  const generateExampleUrl = () => {
    const exampleDomain = domain.trim() || t('qrMenuDomainModal.fields.placeholder');
    return `https://${exampleDomain}/menu/MENU_ID`;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LinkIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="div">
            {t('qrMenuDomainModal.title')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('qrMenuDomainModal.description')}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {currentDomain && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('qrMenuDomainModal.currentDomain')}
                </Typography>
                <Chip 
                  label={currentDomain}
                  color="primary"
                  variant="outlined"
                  icon={<CheckCircleIcon />}
                  sx={{ mb: 2 }}
                />
              </Box>
            )}

            <TextField
              fullWidth
              label={t('qrMenuDomainModal.fields.label')}
              value={domain}
              onChange={(e) => {
                setDomain(e.target.value);
                setError(null);
              }}
              placeholder={t('qrMenuDomainModal.fields.placeholder')}
              helperText={t('qrMenuDomainModal.fields.helper')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
              disabled={saving}
            />

            {domain.trim() && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('qrMenuDomainModal.exampleLink')}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontFamily: 'monospace',
                    bgcolor: 'white',
                    p: 1,
                    borderRadius: 0.5,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    wordBreak: 'break-all'
                  }}
                >
                  {generateExampleUrl()}
                </Typography>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('qrMenuDomainModal.messages.saveSuccess')}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose}
          disabled={saving}
        >
          {t('qrMenuDomainModal.actions.cancel')}
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained"
          disabled={saving || loading || !domain.trim()}
          startIcon={saving ? <CircularProgress size={20} /> : <CheckCircleIcon />}
        >
          {saving ? t('qrMenuDomainModal.actions.saving') : t('qrMenuDomainModal.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QRMenuDomainModal;
